<template>
    <b-modal
        id="attach-document-modal"
        :visible="modalVisible"
        :title="i18nT(`Attach missing invoice document`)"
        @ok="onModalOk"
        @hidden="onModalHidden"
        :ok-disabled="!attachmentSuccess"
        :ok-title="i18nT('Done')"
        :cancel-title="i18nT('Cancel')"
        size="lg"
    >
        <b-row>
            <b-col cols="12" class="mb-1">
                <p class="text-muted mb-0">
                    {{ i18nT(`Attach a scanned expense document to keep this record complete. Drag and drop the file below or click the dropzone to upload.`) }}
                </p>
            </b-col>
            <b-col cols="12" :class="dropzoneHasError ? 'sk-error' : ''">
                <vue-dropzone
                    ref="attachDropzone"
                    id="dz-expense-missing-document"
                    class="sk-dropzone"
                    :options="dropzoneOptions"
                    @vdropzone-sending="dropzoneAddParams"
                    @vdropzone-success="dropzoneSuccess"
                    @vdropzone-error="dropzoneError"
                    @vdropzone-queue-complete="queueComplete"
                    @vdropzone-added="onFileAdded"
                />
                <small class="text-muted d-block mt-50">
                    {{ i18nT(`Accepted format: PDF up to 5 MB.`) }}
                </small>
                <small v-if="dropzoneHasError" class="text-danger d-block mt-50">
                    {{ i18nT(`There was a problem uploading the file. Please try again.`) }}
                </small>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>
import { BModal, BRow, BCol } from 'bootstrap-vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import useAuth from '@/auth/useAuth'
import { API_KEY } from '@core/utils/constants'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
    name: 'AttachExpenseDocumentModal',
    components: {
        BModal,
        BRow,
        BCol,
        VueDropzone: vue2Dropzone,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        expense: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            dropzoneHasError: false,
            attachmentSuccess: false,
        }
    },
    computed: {
        modalVisible: {
            get() {
                return this.visible
            },
            set(val) {
                this.$emit('change', val)
            },
        },
        dropzoneOptions() {
            return {
                url: process.env.VUE_APP_API_URL + 'invoices/invoiceFile',
                paramName: 'invoiceDocument',
                addRemoveLinks: true,
                autoProcessQueue: true,
                clickable: true,
                dictDefaultMessage: this.i18nT(`Drag and drop the expense document here (PDF up to 5.0 MB)`),
                dictRemoveFile: this.i18nT(`Remove`),
                dictFileTooBig: this.i18nT(`File is too big ({{filesize}}MB). Max filesize is: {{maxFilesize}}MB`),
                dictInvalidFileType: this.i18nT(`Only PDFs are allowed`),
                dictCancelUpload: this.i18nT(`Cancel`),
                dictCancelUploadConfirmation: this.i18nT(`Are you sure you want to cancel this upload?`),
                dictMaxFilesExceeded: this.i18nT(`You can only upload 1 file here`),
                maxFilesize: 5,
                maxFiles: 1,
                acceptedFiles: 'application/pdf',
                headers: {
                    'Api-Key': API_KEY,
                    'Auth-Key': useAuth.getToken(),
                },
            }
        },
    },
    watch: {
        visible(val) {
            if (val) this.resetState()
        },
    },
    methods: {
        i18nT(str) {
            return this.$t(str)
        },
        resetState() {
            this.attachmentSuccess = false
            this.dropzoneHasError = false
            this.$nextTick(() => {
                if (this.$refs.attachDropzone) {
                    this.$refs.attachDropzone.removeAllFiles()
                }
            })
        },
        onModalHidden() {
            this.resetState()
            this.modalVisible = false
            this.$emit('close')
        },
        onModalOk(bvModalEvt) {
            if (!this.attachmentSuccess) {
                bvModalEvt.preventDefault()
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: this.i18nT('Please upload a document before closing.'),
                        icon: 'InfoIcon',
                        variant: 'danger',
                    },
                })
            }
        },
        dropzoneAddParams() {
            this.$store.commit('app/REQUEST_PENDING')
        },
        onFileAdded() {
            this.dropzoneHasError = false
            this.attachmentSuccess = false
        },
        queueComplete() {
            if (this.$refs.attachDropzone) {
                this.$refs.attachDropzone.removeAllFiles()
            }
        },
        async dropzoneSuccess(file, response) {
            this.$store.commit('app/REQUEST_DONE')
            this.dropzoneHasError = false

            try {
                await this.attachUploadedInvoiceFile(response)
                this.attachmentSuccess = true

                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: this.i18nT('Expense document attached successfully'),
                        icon: 'CheckIcon',
                        variant: 'success',
                    },
                })

                this.$emit('attached')
                this.$nextTick(() => {
                    this.modalVisible = false
                })
            } catch (error) {
                this.attachmentSuccess = false
            }
        },
        async fetchInvoiceDetails() {
            return this.expense
        },
        appendInvoiceItems(formData, invoice) {
            const items = invoice?.InvoiceItems || []
            items.forEach((item, index) => {
                formData.append(`invoice_items[${index}][id]`, item.Id ?? 0)
                formData.append(`invoice_items[${index}][Title]`, item.Title || item.Label || item.Description || '')
                formData.append(`invoice_items[${index}][UnitPrice]`, item.UnitPrice)
                formData.append(`invoice_items[${index}][Quantity]`, item.Quantity)
                formData.append(`invoice_items[${index}][Amount]`, item.Amount)
                formData.append(`invoice_items[${index}][VatAmount]`, item.VatAmount)
                formData.append(`invoice_items[${index}][VatPercent]`, item.VatPercent)
                formData.append(`invoice_items[${index}][TotalAmount]`, item.TotalAmount)
                formData.append(`invoice_items[${index}][Currency]`, item.Currency || invoice.Currency)
            })
        },
        async attachUploadedInvoiceFile(response) {
            if (!response || !response.data) {
                throw new Error('Missing upload response')
            }

            const fileId = response?.data?.invoice?.InvoiceFile?.Id || response?.data?.InvoiceFile?.Id
            if (!fileId || !this.expense?.Id) {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: this.i18nT('The file was uploaded but the expense could not be updated.'),
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                })
                throw new Error('Missing identifiers')
            }

            const invoiceDetails = await this.fetchInvoiceDetails()

            const formData = new FormData()
            formData.append('id', invoiceDetails.Id)
            formData.append('VendorId', invoiceDetails.VendorId)
            formData.append('InvoiceDate', invoiceDetails.InvoiceDate)
            formData.append('Category', invoiceDetails.Category)
            formData.append('PaymentType', invoiceDetails.PaymentType)
            formData.append('Amount', invoiceDetails.Amount)
            formData.append('TotalAmount', invoiceDetails.TotalAmount)
            formData.append('Currency', invoiceDetails.Currency)
            formData.append('InvoiceFileId', fileId)

            this.appendInvoiceItems(formData, invoiceDetails)

            return this.$http
                .post('invoices', formData)
                .catch(() => {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: this.i18nT('The file was uploaded but could not be attached to the expense.'),
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                        },
                    })
                    throw new Error('Attachment failed')
                })
        },
        dropzoneError(message) {
            this.$store.commit('app/REQUEST_DONE')
            this.attachmentSuccess = false
            this.dropzoneHasError = true

            const fallback = this.i18nT('File upload error')
            let messageText = fallback

            if (message && message.status === 'error' && !message.accepted && message.previewElement && message.previewElement.childNodes && message.previewElement.childNodes[7]) {
                messageText = this.i18nT(message.previewElement.childNodes[7].innerText)
            } else if (message && message.status === 'error' && message.accepted && message.xhr) {
                try {
                    const resp = JSON.parse(message.xhr.response)
                    if (resp && resp.message) {
                        messageText = resp.message
                    }
                } catch (e) {
                    messageText = fallback
                }
            }

            this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                    title: messageText,
                    icon: 'InfoIcon',
                    variant: 'danger',
                },
            })

            setTimeout(() => {
                if (this.$refs.attachDropzone) {
                    this.$refs.attachDropzone.removeAllFiles()
                }
                this.dropzoneHasError = false
            }, 6000)
        },
    },
}
</script>
